// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import TableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
// Styles
import theme from "./styles";

const DEFAULT_ROW_COUNT = 10;

function MuiTable({
  table,
  canSearch,
  canFilter,
  canDownload,
  canViewColumns,
  canJumpToPage,
  canPaginate,
  onDownload,
  onTableChange,
  onFilterChange,
  onChangePage,
  onChangeRowsPerPage,
  rowsPerPageOptions,
  rowsPerPage,
  footerComponent,
  isRemote,
  totalCount,
  sortOrder,
  onRowClick,
  ...props
}) {
  const { t } = useTranslation(["tables"]);
  const currentTheme = useTheme();
  const themeMode = currentTheme.palette.mode;

  const { rows, columns } = table;

  const defaultFooter = (
    count,
    page,
    rowsPerPageValue,
    changeRowsPerPage,
    changePage,
    textLabels
  ) => {
    const rowsPerPageLabel = textLabels?.pagination?.rowsPerPage || t("pagination.rows_per_page");
    const displayRowsLabel = textLabels?.pagination?.displayRows || t("pagination.display_rows");

    return (
      <TableFooter>
        <TableRow>
          <TableCell colSpan={columns.length}>
            <TablePagination
              component="div"
              count={count}
              page={page}
              rowsPerPage={rowsPerPageValue}
              rowsPerPageOptions={rowsPerPageOptions}
              labelRowsPerPage={rowsPerPageLabel}
              labelDisplayedRows={({ from, to, count: displayedCount }) =>
                `${from}-${to} ${displayRowsLabel} ${displayedCount}`
              }
              showFirstButton
              showLastButton
              onPageChange={(_, newPage) => changePage(newPage)}
              onRowsPerPageChange={(event) => changeRowsPerPage(Number(event.target.value))}
            />
          </TableCell>
        </TableRow>
      </TableFooter>
    );
  };

  const options = {
    serverSide: isRemote,
    search: canSearch,
    download: canDownload,
    print: false,
    viewColumns: canViewColumns,
    filter: canFilter,
    rowsPerPage,
    count: totalCount,
    rowsPerPageOptions,
    pagination: canPaginate,
    filterType: "dropdown",
    responsive: "standard",
    tableBodyHeight: "80%",
    selectableRowsHideCheckboxes: true,
    selectToolbarPlacement: "replace",
    selectableRowsOnClick: false,
    jumpToPage: canJumpToPage,
    customFooter: footerComponent || defaultFooter,
    onDownload,
    onFilterChange,
    onTableChange,
    onChangeRowsPerPage,
    onChangePage,
    sortOrder,
    onRowClick,
    textLabels: {
      body: {
        noMatch: t("body.no_match"),
        toolTip: t("body.tooltip"),
        columnHeaderTooltip: (column) => t("body.column_header_tooltip", { label: column.label }),
      },
      pagination: {
        next: t("pagination.next"),
        previous: t("pagination.previous"),
        rowsPerPage: t("pagination.rows_per_page"),
        displayRows: t("pagination.display_rows"),
        jumpToPage: t("pagination.jump_to_page"),
      },
      toolbar: {
        search: t("toolbar.search"),
        downloadCsv: t("toolbar.download_csv"),
        print: t("toolbar.print"),
        viewColumns: t("toolbar.view_columns"),
        filterTable: t("toolbar.filter_table"),
      },
      filter: {
        all: t("filter.all"),
        title: t("filter.title"),
        reset: t("filter.reset"),
      },
      viewColumns: {
        title: t("view_columns.title"),
        titleAria: t("view_columns.title_aria"),
      },
      selectedRows: {
        text: t("selected_rows.text"),
        delete: t("selected_rows.delete"),
        deleteAria: t("selected_rows.delete_aria"),
      },
    },
    ...props,
  };

  return (
    <ThemeProvider
      theme={theme({ rowPointer: onRowClick ? "pointer" : undefined, mode: themeMode })}
    >
      <MUIDataTable data={rows} columns={columns} options={options} />
    </ThemeProvider>
  );
}

MuiTable.defaultProps = {
  canSearch: false,
  canFilter: true,
  canDownload: false,
  canViewColumns: true,
  canJumpToPage: false,
  canPaginate: true,
  footerComponent: null,
  totalCount: null,
  isRemote: false,
  rowsPerPage: DEFAULT_ROW_COUNT,
  rowsPerPageOptions: [5, 10, 15, 20, 25],
  onTableChange: null,
  onDownload: null,
  sortOrder: null,
  onFilterChange: null,
  onChangePage: null,
  onChangeRowsPerPage: null,
  onRowClick: null,
};

MuiTable.propTypes = {
  table: PropTypes.shape({
    // eslint-disable-next-line
    rows: PropTypes.arrayOf(PropTypes.object),
    // eslint-disable-next-line
    columns: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  canSearch: PropTypes.bool,
  canFilter: PropTypes.bool,
  canDownload: PropTypes.bool,
  canViewColumns: PropTypes.bool,
  canJumpToPage: PropTypes.bool,
  canPaginate: PropTypes.bool,
  footerComponent: PropTypes.element,
  isRemote: PropTypes.bool,
  rowsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  onTableChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  onDownload: PropTypes.func,
  onChangePage: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
  onRowClick: PropTypes.func,
  sortOrder: PropTypes.shape({
    name: PropTypes.string,
    direction: PropTypes.string,
  }),
};

export default MuiTable;
